import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { CheckCircle, XCircle, Loader2, Download, Truck, Package, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const [orderDetails, setOrderDetails] = useState(null);

  useEffect(() => {
    if (sessionId) pollStatus();
  }, [sessionId]);

  const pollStatus = async () => {
    const maxAttempts = 10;
    const poll = async (attempt) => {
      if (attempt >= maxAttempts) { setStatus('timeout'); return; }
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          await recordPurchase();
          return;
        }
        if (res.data.status === 'expired') { setStatus('failed'); return; }
        setAttempts(attempt + 1);
        setTimeout(() => poll(attempt + 1), 2000);
      } catch { setStatus('error'); }
    };
    poll(0);
  };

  const recordPurchase = async () => {
    try {
      await api.post('/downloads/purchase', { session_id: sessionId });
      
      // Get order details to show confirmation
      try {
        const ordersRes = await api.get('/orders/my');
        const latestOrder = ordersRes.data?.orders?.find(o => o.stripe_session_id === sessionId);
        if (latestOrder) {
          setOrderDetails(latestOrder);
        }
      } catch {}
      
      setStatus('success');
      toast.success('Purchase confirmed! Your order is ready.');
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.detail === 'Not a product purchase') {
        setStatus('success');
      } else {
        setStatus('success');
      }
    }
  };

  const isPodOrder = orderDetails?.is_pod_product;

  return (
    <div className="max-w-lg mx-auto text-center py-12 animate-in" data-testid="payment-success-page">
      {status === 'checking' && (
        <>
          <Loader2 size={48} className="mx-auto text-gold animate-spin mb-6" />
          <h1 className="font-serif text-2xl mb-2">Processing Payment</h1>
          <p className="text-sm text-muted-foreground">Verifying your payment and preparing your order... (attempt {attempts + 1})</p>
        </>
      )}
      
      {status === 'success' && (
        <>
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="font-serif text-2xl md:text-3xl mb-2">Payment Successful!</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Thank you for your purchase. Your order has been confirmed.
          </p>
          
          {/* Order Summary */}
          {orderDetails && (
            <div className="bg-mist border border-border/40 p-6 mb-6 text-left">
              <h2 className="font-serif text-lg mb-4">Order Summary</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Product:</span>
                  <span className="font-medium">{orderDetails.title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount Paid:</span>
                  <span className="font-medium text-gold">€{orderDetails.price?.toFixed(2)}</span>
                </div>
                {isPodOrder && orderDetails.pod_options && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Size:</span>
                      <span className="font-medium">{orderDetails.pod_options.size || 'M'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Color:</span>
                      <span className="font-medium">{orderDetails.pod_options.color || 'White'}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Order Status:</span>
                  <span className="font-medium capitalize">{orderDetails.order_status?.replace(/_/g, ' ') || 'Paid'}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Next Steps for POD */}
          {isPodOrder && (
            <div className="bg-purple-50 border border-purple-200 p-5 mb-6 rounded-sm text-left">
              <div className="flex items-start gap-3">
                <Package className="h-6 w-6 text-purple-600 mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-medium text-purple-800 mb-1">Print-on-Demand Order</h3>
                  <p className="text-sm text-purple-600 mb-2">
                    Your custom product is ready to be printed! Please provide your shipping address to complete the order.
                  </p>
                  <div className="flex items-center gap-2 text-xs text-purple-500">
                    <Truck size={14} />
                    <span>Estimated delivery: 5-7 business days after shipping submitted</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Next Steps for Digital */}
          {!isPodOrder && orderDetails && (
            <div className="bg-blue-50 border border-blue-200 p-5 mb-6 rounded-sm text-left">
              <div className="flex items-start gap-3">
                <Download className="h-6 w-6 text-blue-600 mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-medium text-blue-800 mb-1">Digital Download Ready</h3>
                  <p className="text-sm text-blue-600">
                    Your files are ready for download. Head to your purchases to access them.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/downloads">
              <Button 
                data-testid="go-to-downloads" 
                className="w-full sm:w-auto rounded-sm text-xs uppercase tracking-wider bg-gold text-obsidian hover:bg-obsidian hover:text-white transition-colors duration-300"
              >
                {isPodOrder ? (
                  <>
                    <Truck size={14} className="mr-2" /> Add Shipping Info
                  </>
                ) : (
                  <>
                    <Download size={14} className="mr-2" /> Go to My Purchases
                  </>
                )}
              </Button>
            </Link>
            <Link to="/marketplace">
              <Button 
                data-testid="back-to-marketplace-btn" 
                variant="outline" 
                className="w-full sm:w-auto rounded-sm text-xs uppercase tracking-wider"
              >
                Continue Shopping <ArrowRight size={14} className="ml-2" />
              </Button>
            </Link>
          </div>
        </>
      )}
      
      {(status === 'failed' || status === 'error' || status === 'timeout') && (
        <>
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <XCircle className="h-10 w-10 text-red-600" />
          </div>
          <h1 className="font-serif text-2xl mb-2">Payment Issue</h1>
          <p className="text-sm text-muted-foreground mb-8">There was an issue processing your payment. Please try again.</p>
          <Link to="/marketplace">
            <Button data-testid="retry-marketplace-btn" className="rounded-sm text-xs uppercase tracking-wider bg-obsidian hover:bg-gold hover:text-obsidian">
              Back to Marketplace
            </Button>
          </Link>
        </>
      )}
    </div>
  );
}
